/**
 * Market intelligence — RM-captured insight about the market, tagged to the
 * parts of the book it bears on.
 *
 * One item is a single insight ("Target is adding Apparel shelf space"), either
 * typed by an RM or extracted from a call transcript. Each item carries zero or
 * more tags, and a tag points at ONE of: an industry, an industry/sub-industry
 * pair, a client, a company, a person, or an organization. An industry-tagged
 * item therefore surfaces on every client in that industry without being
 * duplicated per client.
 *
 * Deliberately separate from the `newsfeed_*` tables: those hold automated
 * newsletter content, this holds human/agent insight. The two are never merged,
 * and nothing here is client-facing or written back to Salesforce.
 */
/** Where an item came from. Text on the row so a new source needs no migration. */
export enum MarketIntelligenceSourceType {
  Manual = 'manual',
  FirefliesTranscript = 'fireflies_transcript',
  /** A bullet from the weekly Portfolio News post on Salesforce Chatter. */
  SalesforceChatter = 'salesforce_chatter',
  /** A Market News headline, condensed into a line. */
  MarketNews = 'market_news',
}

/**
 * What one tag points at. `Industry` matches on industry alone; `SubIndustry`
 * matches on the industry AND sub-industry together, because sub-industry labels
 * are not unique across parents ("Food" exists under Child/Baby, Four-wall and
 * Pet Products; "Other" exists under nearly every industry).
 */
export enum MarketIntelligenceTagKind {
  Industry = 'industry',
  SubIndustry = 'sub_industry',
  Client = 'client',
  Company = 'company',
  Person = 'person',
  Organization = 'organization',
}

/** Entity-reference tag kinds — the ones resolved against a CRM record. */
export const MARKET_INTELLIGENCE_ENTITY_TAG_KINDS = [
  MarketIntelligenceTagKind.Client,
  MarketIntelligenceTagKind.Company,
  MarketIntelligenceTagKind.Person,
  MarketIntelligenceTagKind.Organization,
]

/** The CRM records a name typed into the tag picker can become. Clients are onboarded, not typed. */
export const MARKET_INTELLIGENCE_CREATABLE_TAG_KINDS = [
  MarketIntelligenceTagKind.Organization,
  MarketIntelligenceTagKind.Company,
  MarketIntelligenceTagKind.Person,
] as const

/** Who put the tag on the item. Drives the "needs review" queue. */
export enum MarketIntelligenceTagSource {
  Auto = 'auto',
  Human = 'human',
}

export interface IMarketIntelligenceTag {
  id: string
  itemId: string
  kind: MarketIntelligenceTagKind
  /** Set for Industry and SubIndustry kinds. */
  industry: string | null
  /** Set for SubIndustry only, alongside `industry`. */
  subIndustry: string | null
  clientId: string | null
  companyId: string | null
  personId: string | null
  organizationId: string | null
  /** The name as it was said, kept when the reference resolved to nothing. */
  rawName: string | null
  resolutionConfidence: number | null
  source: MarketIntelligenceTagSource
  /** Stable identity of what this tag points at, unique within an item. */
  tagKey: string
  createdAt: Date | string
  updatedAt: Date | string
}

export interface IMarketIntelligenceItem {
  id: string
  text: string
  occurredAt: Date | string | null
  sourceType: MarketIntelligenceSourceType | string
  sourceId: string | null
  verbatimQuote: string | null
  createdByUserId: string | null
  extractionConfidence: number | null
  dedupeKey: string | null
  createdAt: Date | string
  updatedAt: Date | string
}

/** A tag flattened for display — the label the UI shows, plus where it points. */
export interface IMarketIntelligenceTagView extends IMarketIntelligenceTag {
  /** Human-readable label ("Apparel", "Food › Snacks", a client or person name). */
  label: string
  /** The resolved record's id, whichever kind it is. Null for taxonomy tags. */
  resolvedId: string | null
  isResolved: boolean
}

/** One row in the portfolio list. */
export interface IMarketIntelligenceRow extends IMarketIntelligenceItem {
  tags: IMarketIntelligenceTagView[]
  /** The call's title, or the source's name — see SOURCE_TYPE_LABEL. */
  sourceLabel: string
  /** Deep link to the source transcript, when there is one. */
  sourceUrl: string | null
  authorName: string | null
}

/** Payload a tag picker submits — exactly one of these shapes is populated. */
export interface IMarketIntelligenceTagInput {
  kind: MarketIntelligenceTagKind
  industry?: string | null
  subIndustry?: string | null
  entityId?: string | null
  /**
   * The record's name, for a caller that has one but not its id — a person
   * typing "tag this to Olipop", or Arc relaying a candidate. Resolved server
   * side against the same matcher the extractor uses; an unresolvable name is
   * still stored, so an RM can correct it rather than losing the tag.
   */
  rawName?: string | null
}

/** Longest an item's text may be. Keeps the list readable. */
export const MARKET_INTELLIGENCE_TEXT_MAX_LENGTH = 1000
